import { useState, lazy, Suspense, type ReactNode } from "react";
import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ResponsiveSidebar } from "@/components/layout/responsive-sidebar";
import Header from "@/components/layout/header";
import { LanguageProvider, useLanguage } from "@/hooks/use-language";
import { AuthProvider, useAuth } from "@/hooks/use-auth";
import { useCompanyTheme } from "@/hooks/use-company-theme";
const Login = lazy(() => import("@/pages/login"));

// Lazy-loaded pages for optimal performance and code-splitting
const NotFound = lazy(() => import("@/pages/not-found"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const PropertyDetails = lazy(() => import("@/pages/property-details"));
const Tenants = lazy(() => import("@/pages/tenants"));
const TenantDetails = lazy(() => import("@/pages/tenant-details"));
const Properties = lazy(() => import("@/pages/properties"));
const SMS = lazy(() => import("@/pages/sms"));
const Reports = lazy(() => import("@/pages/reports"));
const Backup = lazy(() => import("@/pages/backup"));
const Maintenance = lazy(() => import("@/pages/maintenance"));
const WhatsApp = lazy(() => import("@/pages/whatsapp"));
const GoogleContacts = lazy(() => import("@/pages/google-contacts"));
const ImportTenants = lazy(() => import("@/pages/import-tenants"));
const BuildingLayout = lazy(() => import("@/pages/building-layout"));
const Blacklist = lazy(() => import("@/pages/blacklist"));
const DebtManagement = lazy(() => import("@/pages/debt-management"));
const DebtRepayment = lazy(() => import("@/pages/debt-repayment"));
const BusinessInsights = lazy(() => import("@/pages/business-insights"));
const Expenses = lazy(() => import("@/pages/expenses"));
const Invoices = lazy(() => import("@/pages/invoices"));
const FinancialReports = lazy(() => import("@/pages/financial-reports"));
const CashReconciliation = lazy(() => import("@/pages/cash-reconciliation"));
const Phonebook = lazy(() => import("@/pages/phonebook"));
const TenantLite = lazy(() => import("@/pages/lite/tenant-lite"));
const MobileApp = lazy(() => import("@/pages/mobile/index"));
const AuditLog = lazy(() => import("@/pages/audit-log"));
const PublicBooking = lazy(() => import("@/pages/public-booking"));
const LeadFormPage = lazy(() => import("@/pages/lead-form"));
const Reservations = lazy(() => import("@/pages/reservations"));
const ScheduledAlerts = lazy(() => import("@/pages/scheduled-alerts"));
const SaasHub = lazy(() => import("@/pages/saas-hub"));
const LandingPage = lazy(() => import("@/pages/landing"));
const CompanySettings = lazy(() => import("@/pages/company-settings"));
const TeamManagement = lazy(() => import("@/pages/team-management"));
const TenantPortal = lazy(() => import("@/pages/tenant-portal"));
const WhatsAppAI = lazy(() => import("@/pages/whatsapp-ai"));
const MetaInbox = lazy(() => import("@/pages/meta-inbox"));
const SubscriptionBilling = lazy(() => import("@/pages/subscription-billing"));
const AdvancedAnalytics = lazy(() => import("@/pages/advanced-analytics"));

function PageLoader() {
  return (
    <div className="flex h-64 items-center justify-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-mint"></div>
    </div>
  );
}

function FullScreenLoader() {
  return (
    <div className="flex h-screen items-center justify-center bg-ink">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-mint"></div>
    </div>
  );
}

// Gates the internal admin surfaces (main dashboard + SaaS hub) behind a real
// staff login, and applies the logged-in user's company branding (white-label).
function Protected({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, user } = useAuth();
  useCompanyTheme(isAuthenticated ? user?.company : null);

  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<FullScreenLoader />}>
        <Login />
      </Suspense>
    );
  }
  return children;
}

function Router() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { dir } = useLanguage();

  return (
    <div dir={dir} className="flex h-screen overflow-hidden bg-background text-foreground font-sans">
      <ResponsiveSidebar 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header onMobileMenuClick={() => setMobileMenuOpen(true)} />
        <main className="flex-1 overflow-y-auto focus:outline-none">
          <Suspense fallback={<PageLoader />}>
            <Switch>
              <Route path="/" component={Dashboard} />
              <Route path="/properties" component={Properties} />
              <Route path="/website-units" component={Properties} />
              <Route path="/properties/:id" component={PropertyDetails} />
              <Route path="/tenants" component={Tenants} />
              <Route path="/tenants/:id" component={TenantDetails} />
              <Route path="/rentals" component={Properties} />
              <Route path="/sms" component={SMS} />

              <Route path="/payments" component={Tenants} />
              <Route path="/payment" component={Tenants} />
              <Route path="/monthly-payments" component={Tenants} />
              <Route path="/tenant-payments" component={Tenants} />
              <Route path="/contracts" component={Tenants} />
              <Route path="/maintenance" component={Maintenance} />
              <Route path="/whatsapp" component={WhatsApp} />
              <Route path="/meta-inbox" component={MetaInbox} />
              <Route path="/facebook" component={MetaInbox} />
              <Route path="/messenger" component={MetaInbox} />
              <Route path="/scheduled-alerts" component={ScheduledAlerts} />
              <Route path="/sms-automation" component={ScheduledAlerts} />
              <Route path="/google-contacts" component={GoogleContacts} />
              <Route path="/import-tenants" component={ImportTenants} />
              <Route path="/rental" component={BuildingLayout} />
              <Route path="/blacklist" component={Blacklist} />
              <Route path="/debt-management" component={DebtManagement} />
              <Route path="/debt-repayment" component={DebtRepayment} />
              <Route path="/expenses" component={Expenses} />
              <Route path="/invoices" component={Invoices} />
              <Route path="/business-insights" component={BusinessInsights} />
              <Route path="/audit-log" component={AuditLog} />
              <Route path="/reports" component={Reports} />
              <Route path="/financial-reports" component={FinancialReports} />
              <Route path="/cash-reconciliation" component={CashReconciliation} />
              <Route path="/phonebook" component={Phonebook} />
              <Route path="/backup" component={Backup} />
              <Route path="/admin/reservations" component={Reservations} />
              <Route path="/saas" component={SaasHub} />
              <Route path="/companies" component={SaasHub} />
              <Route path="/company-settings" component={CompanySettings} />
              <Route path="/branding" component={CompanySettings} />
              <Route path="/team" component={TeamManagement} />
              <Route path="/team-management" component={TeamManagement} />
              <Route path="/whatsapp-ai" component={WhatsAppAI} />
              <Route path="/ai-bot" component={WhatsAppAI} />
              <Route path="/subscription" component={SubscriptionBilling} />
              <Route path="/billing" component={SubscriptionBilling} />
              <Route path="/advanced-analytics" component={AdvancedAnalytics} />
              <Route path="/predictive" component={AdvancedAnalytics} />
              <Route path="/lead" component={LeadFormPage} />
              <Route path="/lead-form" component={LeadFormPage} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function LiteRouter() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/lite" component={TenantLite} />
        <Route path="/lite/:rest*" component={TenantLite} />
      </Switch>
    </Suspense>
  );
}

function PublicRouter() {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const isMarketingHost = host === "rently.cc" || host === "www.rently.cc";
  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/signup" component={LandingPage} />
        <Route path="/lead" component={LeadFormPage} />
        <Route path="/lead-form" component={LeadFormPage} />
        <Route path="/reserve-lead" component={LeadFormPage} />
        <Route path="/book/lead" component={LeadFormPage} />
        {isMarketingHost && <Route path="/" component={LandingPage} />}
        <Route path="/book" component={PublicBooking} />
        <Route path="/book/:rest*" component={PublicBooking} />
        <Route path="/" component={PublicBooking} />
        <Route path="/:rest*" component={PublicBooking} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  
  const isLiteMode = path.startsWith("/lite");
  const isMobileMode = path.startsWith("/mobile");
  const isTenantPortal = path === "/tenant-portal" || path === "/pay";
  const isSaasHub = host === "biz.rently.cc" || path === "/saas" || path === "/companies";
  const isPublicMode = (host === "rently.cc" || host === "www.rently.cc" || path.startsWith("/book") || path.startsWith("/lead") || path.startsWith("/lead-form") || path.startsWith("/reserve-lead") || path.startsWith("/signup")) && !isSaasHub;

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Suspense fallback={<PageLoader />}>
            {isMobileMode ? (
              <MobileApp />
            ) : isLiteMode ? (
              <LiteRouter />
            ) : isTenantPortal ? (
              <TenantPortal />
            ) : isSaasHub ? (
              <Protected><SaasHub /></Protected>
            ) : isPublicMode ? (
              <PublicRouter />
            ) : (
              <Protected><Router /></Protected>
            )}
          </Suspense>
        </TooltipProvider>
      </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
